import { recordEntries } from '@seen/core/utils'

import { Button, cn, KBD, TypographyDetail } from '@seen/ui'
import { IconBookmark, IconFilter, IconTrash } from '@tabler/icons-react'
import * as React from 'react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ComboboxMultiCommand } from '#src/components/core/combobox/ComboboxMulti.tsx'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '#src/components/core/Command.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import Filters, {
  AnimateChangeInHeight,
  type Filter,
  type FilterConfig,
} from '#src/components/filter/Filter.tsx'
import {
  FILTER_OPERATORS,
  type FilterOperator,
} from '#src/components/filter/operators.ts'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'

// Type for saved filter configurations
type SavedFilterConfig<Fields extends string> = {
  name: string
  filters: Filter<Fields>[]
}

// Local storage utility functions
const getSavedFilters = <Fields extends string>(
  id: string
): SavedFilterConfig<Fields>[] => {
  try {
    const savedFilters = localStorage.getItem(`saved-filters-${id}`)
    if (savedFilters) {
      return JSON.parse(savedFilters) as SavedFilterConfig<Fields>[]
    }
  } catch (error) {
    console.error(
      `Error loading saved filters from local storage (id: ${id}):`,
      error
    )
  }
  return []
}

const saveFilterConfig = <Fields extends string>(
  id: string,
  name: string,
  filters: Filter<Fields>[]
): void => {
  try {
    const savedFilters = getSavedFilters<Fields>(id)
    const existingIndex = savedFilters.findIndex(
      (config) => config.name === name
    )

    if (existingIndex >= 0) {
      // Update existing filter config
      savedFilters[existingIndex] = { name, filters }
    } else {
      // Add new filter config
      savedFilters.push({ name, filters })
    }

    localStorage.setItem(`saved-filters-${id}`, JSON.stringify(savedFilters))
  } catch (error) {
    console.error(
      `Error saving filter configuration to local storage (id: ${id}):`,
      error
    )
  }
}

const deleteFilterConfig = <Fields extends string>(
  id: string,
  name: string
): void => {
  try {
    const savedFilters = getSavedFilters<Fields>(id)
    const updatedFilters = savedFilters.filter((config) => config.name !== name)
    localStorage.setItem(`saved-filters-${id}`, JSON.stringify(updatedFilters))
  } catch (error) {
    console.error(
      `Error deleting filter configuration from local storage (id: ${id}):`,
      error
    )
  }
}

type FilterComposerProps<T, Fields extends string> = {
  id: string
  filterOptions: {
    [Field in Fields]: FilterConfig<T>
  }
  onChange: (filters: Filter<Fields>[]) => void
}

export function FilterComposer<T, Fields extends string = string>({
  id,
  filterOptions,
  onChange,
}: FilterComposerProps<T, Fields>): JSX.Element {
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const [open, setOpen] = useState(false)
  const [selectedView, setSelectedView] = useState<Fields | null>(null)
  const [commandInput, setCommandInput] = useState('')
  const commandInputRef = useRef<HTMLInputElement>(null)
  const [filters, setFilters] = useState<Filter<Fields>[]>([])
  const [savedFilters, setSavedFilters] = useState<SavedFilterConfig<Fields>[]>(
    []
  )
  const [saveDialogOpen, setSaveDialogOpen] = useState(false)
  const [saveFilterName, setSaveFilterName] = useState('')

  useEffect(() => {
    const loadedFilters = getSavedFilters<Fields>(id)
    setSavedFilters(loadedFilters)
  }, [id])

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search)
      const storedFilters = searchParams.get(id)
      if (storedFilters) {
        const parsedFilters = JSON.parse(storedFilters) as Filter<Fields>[]
        setFilters(parsedFilters)
      }
    } catch (error) {
      console.error(`Error loading filters from URL (id: ${id}):`, error)
    }
  }, [id])

  useEffect(() => {
    try {
      const searchParams = new URLSearchParams(window.location.search)
      if (filters.length > 0) {
        searchParams.set(id, JSON.stringify(filters))
      } else {
        searchParams.delete(id)
      }

      const query = searchParams.toString()
        ? `?${searchParams.toString()}`
        : window.location.pathname
      window.history.replaceState(null, '', query + window.location.hash)
    } catch (error) {
      console.error(`Error updating URL with filters (id: ${id}):`, error)
    }

    onChangeRef.current(filters)
  }, [filters, id])

  const filtersInView = filters.filter((f) => f.field === selectedView)
  const anyFiltersApplied = filters.flatMap((f) => f.value).length > 0
  const [showFilterTriggerText, setShowFilterTriggerText] =
    useState(anyFiltersApplied)

  useEffect(() => {
    // Prevent content shift when the popover is opened by deferring the 'any filters applied'
    // check until the popover is closed
    if (open) {
      return
    }

    setShowFilterTriggerText(anyFiltersApplied)
  }, [anyFiltersApplied, open])

  const selectedFilterConfig = selectedView && filterOptions[selectedView]
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogValue, setDialogValue] = useState(
    filtersInView.find((f) => f.field === selectedView)?.value[0] ?? ''
  )

  const handleSaveFilter = (): void => {
    if (saveFilterName.trim() && filters.length > 0) {
      saveFilterConfig(id, saveFilterName, filters)
      setSaveFilterName('')
      setSaveDialogOpen(false)

      setSavedFilters(getSavedFilters<Fields>(id))
    }
  }

  const handleLoadFilter = (savedConfig: SavedFilterConfig<Fields>): void => {
    setFilters(savedConfig.filters)
    setOpen(false)
  }

  const handleDeleteFilter = (name: string, e: React.MouseEvent): void => {
    e.stopPropagation()
    deleteFilterConfig(id, name)

    setSavedFilters(getSavedFilters<Fields>(id))
  }

  useHotkeys(Hotkeys.Filter, () => setOpen(true), {
    preventDefault: true,
  })

  return (
    <div className="flex flex-wrap gap-2">
      <Dialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open)
          if (!open) {
            setDialogValue('')
            setSelectedView(null)
          }
        }}
      >
        <DialogDisplay>
          <DialogHeader>
            <DialogTitle>Filter by {selectedFilterConfig?.label}…</DialogTitle>
          </DialogHeader>
          <DialogContent>
            <DialogDescription>
              <Input
                value={dialogValue}
                onChange={(e) => {
                  setDialogValue(e.target.value)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && selectedView) {
                    setDialogOpen(false)
                    setDialogValue('')
                    setSelectedView(null)
                    setFilters((prev) => {
                      const notInView = prev.filter(
                        (f) => f.field !== selectedView
                      )

                      return [
                        ...notInView,
                        {
                          field: selectedView,
                          value: [dialogValue],
                          operator: 'contains',
                        },
                      ]
                    })
                  }
                }}
              />
            </DialogDescription>
          </DialogContent>
        </DialogDisplay>
      </Dialog>

      <Dialog
        open={saveDialogOpen}
        onOpenChange={(open) => {
          setSaveDialogOpen(open)
          if (!open) {
            setSaveFilterName('')
          }
        }}
      >
        <DialogDisplay>
          <DialogHeader asChild>
            <DialogTitle>Save Filter Configuration</DialogTitle>
          </DialogHeader>
          <DialogContent className="py-0">
            <DialogDescription>
              <p className="mb-4 text-sm text-muted-foreground">
                Enter a name for this filter configuration. If a configuration
                with this name already exists, it will be overwritten.
              </p>
              <Input
                value={saveFilterName}
                onChange={(e) => setSaveFilterName(e.target.value)}
                placeholder="Filter name"
                className="mb-2"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSaveFilter()
                  }
                }}
              />
            </DialogDescription>
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSaveDialogOpen(false)}
              size="sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveFilter}
              disabled={!saveFilterName.trim() || filters.length === 0}
              size="sm"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogDisplay>
      </Dialog>

      <Popover
        open={open}
        onOpenChange={(open) => {
          setOpen(open)
          if (!open) {
            setTimeout(() => {
              setSelectedView(null)
              setCommandInput('')
            }, 200)
          }
        }}
      >
        <Tooltip>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={open}
                size="sm"
                className={cn(
                  'group flex h-6 items-center gap-1.5 rounded-sm text-xs transition',
                  showFilterTriggerText && 'w-6'
                )}
              >
                <IconFilter className="size-3 shrink-0 text-muted-foreground transition-all group-hover:text-primary" />
                {!showFilterTriggerText && 'Filter'}
              </Button>
            </TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent className="flex items-center gap-2 px-2">
            <TypographyDetail className="text-muted-foreground">
              Filter
            </TypographyDetail>
            <KBD>F</KBD>
          </TooltipContent>
        </Tooltip>
        <PopoverContent className="w-[250px] p-0">
          <AnimateChangeInHeight>
            {!selectedView && (
              <Command>
                <CommandInput
                  placeholder={selectedView ? selectedView : 'Filter...'}
                  className="h-9"
                  value={commandInput}
                  onInputCapture={(e) => {
                    setCommandInput(e.currentTarget.value)
                  }}
                  ref={commandInputRef}
                />
                <CommandList>
                  <CommandEmpty>No results found.</CommandEmpty>

                  {/* Saved Filters Group */}
                  {savedFilters.length > 0 && (
                    <CommandGroup heading="Saved Filters">
                      {savedFilters.map((savedConfig, index) => (
                        <CommandItem
                          key={`saved-${index}`}
                          className="group flex items-center justify-between gap-2 text-muted-foreground"
                          value={`saved-${savedConfig.name}`}
                          onSelect={() => handleLoadFilter(savedConfig)}
                        >
                          <div className="flex items-center gap-2">
                            <IconBookmark className="size-3.5 shrink-0" />
                            <span className="text-accent-foreground">
                              {savedConfig.name}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 p-0 opacity-0 transition group-hover:opacity-100"
                            onClick={(e) =>
                              handleDeleteFilter(savedConfig.name, e)
                            }
                          >
                            <IconTrash className="size-3.5 text-destructive" />
                          </Button>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}

                  {/* Filter Fields Group */}
                  <CommandGroup heading="Filter Fields">
                    {recordEntries(filterOptions).map(
                      ([name, { icon, label }], index: number) => (
                        <React.Fragment key={index}>
                          <CommandItem
                            className="group flex items-center gap-2 text-muted-foreground"
                            key={name}
                            value={name}
                            keywords={[name, label ?? '']}
                            onSelect={(currentValue) => {
                              setSelectedView(currentValue as Fields)
                              const type =
                                filterOptions[currentValue as Fields].type

                              setCommandInput('')
                              if (
                                type === 'multi-select' ||
                                type === 'multi-select-array' ||
                                type === 'date'
                              ) {
                                commandInputRef.current?.focus()
                              } else if (type === 'text') {
                                setOpen(false)
                                setDialogOpen(true)
                              } else if (type === 'boolean') {
                                setFilters((prev) => {
                                  const notInView = prev.filter(
                                    (f) => f.field !== currentValue
                                  )
                                  return [
                                    ...notInView,
                                    {
                                      field: currentValue as Fields,
                                      value: ['true'],
                                      operator: 'equals',
                                    },
                                  ]
                                })
                                setSelectedView(null)
                                setOpen(false)
                              }
                            }}
                          >
                            {icon}
                            <span className="text-accent-foreground">
                              {label ?? name}
                            </span>
                          </CommandItem>
                        </React.Fragment>
                      )
                    )}
                  </CommandGroup>
                </CommandList>
              </Command>
            )}

            {selectedView &&
              (selectedFilterConfig?.type === 'multi-select' ||
                selectedFilterConfig?.type === 'multi-select-array') && (
                <ComboboxMultiCommand
                  options={selectedFilterConfig.options.map((o) => ({
                    value: o.value,
                    label: o.label ?? o.value,
                    keywords: o.keywords,
                  }))}
                  onSelect={(values, close) => {
                    setFilters((prev) => {
                      const notInView = prev.filter(
                        (f) => f.field !== selectedView
                      )
                      const inView = prev.filter(
                        (f) => f.field === selectedView
                      )?.[0] ?? {
                        field: selectedView,
                        value: [],
                        operator: 'in',
                      }
                      return [
                        ...notInView,
                        {
                          ...inView,
                          value: values,
                        },
                      ]
                    })
                    if (close) {
                      setSelectedView(null)
                      setOpen(false)
                    }
                  }}
                  selectedValues={filtersInView.flatMap((f) => f.value)}
                />
              )}
            {selectedView && selectedFilterConfig?.type === 'date' && (
              <DateFilterEditor
                filter={
                  filtersInView[0] ?? {
                    field: selectedView,
                    value: [''],
                    operator: 'equals',
                  }
                }
                onChange={(filter) => {
                  const notInView = filters.filter(
                    (f) => f.field !== selectedView
                  )
                  setFilters([...notInView, filter])
                  setSelectedView(null)
                  setOpen(false)
                }}
              />
            )}
            {selectedView && selectedFilterConfig?.type === 'boolean' && (
              <div className="flex flex-col gap-2 p-2">
                <Combobox
                  options={[
                    { value: 'true', label: 'True', keywords: ['true', 'yes'] },
                    {
                      value: 'false',
                      label: 'False',
                      keywords: ['false', 'no'],
                    },
                  ]}
                  onSelect={(v) => {
                    setFilters((prev) => {
                      const notInView = prev.filter(
                        (f) => f.field !== selectedView
                      )
                      return [
                        ...notInView,
                        {
                          field: selectedView,
                          value: [v],
                          operator: 'equals',
                        },
                      ]
                    })
                    setSelectedView(null)
                    setOpen(false)
                  }}
                >
                  <Button variant="outline" className="w-full text-left">
                    {(() => {
                      const val = filtersInView[0]?.value[0]
                      if (val === 'true') {
                        return 'True'
                      }
                      if (val === 'false') {
                        return 'False'
                      }
                      return 'Select...'
                    })()}
                  </Button>
                </Combobox>
              </div>
            )}
          </AnimateChangeInHeight>
        </PopoverContent>
      </Popover>
      <Filters
        filters={filters}
        setFilters={setFilters}
        filterOptions={filterOptions}
      />
      {filters.filter((filter) => filter.value?.length > 0).length > 0 && (
        <>
          <Button
            variant="outline"
            size="sm"
            className="group h-6 items-center rounded-sm text-xs transition"
            onClick={() => setFilters([])}
          >
            Clear
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="group flex h-6 items-center gap-1.5 rounded-sm text-xs transition"
            onClick={() => setSaveDialogOpen(true)}
            disabled={filters.length === 0}
          >
            <IconBookmark className="size-3 shrink-0 text-muted-foreground transition-all group-hover:text-primary" />
            Save
          </Button>
        </>
      )}
    </div>
  )
}

type DateFilterEditorProps<Fields extends string> = {
  filter: Filter<Fields>
  onChange: (filter: Filter<Fields>) => void
}

function DateFilterEditor<Fields extends string>({
  filter,
  onChange,
}: DateFilterEditorProps<Fields>): JSX.Element {
  const operators = FILTER_OPERATORS.date
  const [operator, setOperator] = React.useState<FilterOperator>(
    filter.operator ?? 'equals'
  )
  const [value, setValue] = React.useState<string[]>(
    filter.value.length > 0 ? filter.value : ['']
  )

  React.useEffect(() => {
    // Reset value if operator changes
    if (operator === 'between' && value.length < 2) {
      setValue(['', ''])
    } else if (operator !== 'between' && value.length > 1) {
      setValue([value[0]])
    }
  }, [operator, value])

  const handleApply = (): void => {
    onChange({
      ...filter,
      operator,
      value,
    })
  }

  // Use Combobox for operator selection
  const operatorOptions = operators.map((op) => ({
    value: op.value,
    label: op.label,
    keywords: [op.label.toLowerCase(), op.value],
  }))

  return (
    <div className="flex w-full max-w-full flex-col gap-2 p-2">
      <div className="flex w-full max-w-full flex-wrap items-center gap-2">
        <Combobox
          options={operatorOptions}
          onSelect={(val) => setOperator(val as FilterOperator)}
        >
          <button
            type="button"
            className="w-full rounded border bg-background px-2 py-1 text-left text-xs"
          >
            {operatorOptions.find((op) => op.value === operator)?.label}
          </button>
        </Combobox>
        {operator === 'between' ? (
          <div className="flex flex-wrap items-center gap-2">
            <DatePicker
              value={value[0] || null}
              onChange={(v) => setValue([v ?? '', value[1] ?? ''])}
              placeholder="From"
            />
            <DatePicker
              value={value[1] || null}
              onChange={(v) => setValue([value[0] ?? '', v ?? ''])}
              placeholder="To"
            />
          </div>
        ) : (
          <div className="max-w-[160px] min-w-[120px] flex-1">
            <DatePicker
              value={value[0] || null}
              onChange={(v) => setValue([v ?? ''])}
              placeholder="Date"
            />
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={handleApply}>
          Apply
        </Button>
      </div>
    </div>
  )
}
